import type { Decision, Lane, WorkItem, WorkOrder, WorkOrderState } from "@szyos/contracts";
import { ACTIVE_RUN_STATES } from "./activity";
import { ITEM_DONE, PHASES, WORK_ORDER_INTERRUPTS, phaseOf, type Phase } from "./machines";

/** Darstellungsstatus eines Knotens in der Flow Map (docs/16 §5). */
export type NodeStatus = "done" | "run" | "wait" | "block" | "gate";

export const LANE_LABELS: Record<Lane, string> = {
  research: "Research",
  design: "UI / UX Design",
  code: "Code",
  test: "Test & QA",
  docs: "Doku",
};

export interface LaneView {
  lane: Lane;
  label: string;
  done: number;
  total: number;
  status: NodeStatus;
  providers: string[];
}

export interface StepView {
  key: Phase;
  label: string;
  status: "done" | "current" | "pending";
  gate: boolean;
}

export interface FlowModel {
  phase: Phase;
  steps: StepView[];
  briefing: NodeStatus;
  orchestrator: NodeStatus;
  lanes: LaneView[];
  gate: NodeStatus;
  pr: NodeStatus;
  staging: NodeStatus;
  production: NodeStatus;
  openDecisions: Decision[];
  /** Angehalten: keine neuen Läufe, bis der Owner fortsetzt. */
  held: boolean;
}

/**
 * Tatsächliche Läufe des Auftrags. Mit ihnen gilt nur als „läuft“, was ein aktiver Lauf belegt;
 * eine Aufgabe, deren Lauf in der Warteschlange steht oder deren Auftrag angehalten ist, wartet.
 */
export interface FlowLive {
  runs: readonly { workItemId: string | null; state: string }[];
  /** Ein Planvorschlag oder eine Klärung läuft gerade im Studio. */
  studioActive: boolean;
}

const ORDER: readonly WorkOrderState[] = [
  "DRAFT",
  "SPEC_REVIEW",
  "PLAN_REVIEW",
  "READY",
  "RUNNING",
  "VERIFYING",
  "REVIEW_READY",
  "ACCEPTED",
  "STAGING",
  "RELEASE_APPROVAL",
  "RELEASED",
];

function rank(state: WorkOrderState | null): number {
  return state ? ORDER.indexOf(state) : -1;
}

const HUMAN_GATES: ReadonlySet<WorkOrderState> = new Set([
  "SPEC_REVIEW",
  "PLAN_REVIEW",
  "REVIEW_READY",
  "RELEASE_APPROVAL",
  "WAITING_HUMAN",
]);

function laneStatus(items: readonly WorkItem[], active: ReadonlySet<string> | null): NodeStatus {
  if (items.every((i) => ITEM_DONE.has(i.state))) return "done";
  if (items.some((i) => i.state === "blocked" || i.state === "failed")) return "block";
  if (active) {
    // „verifying“: szyOS prüft die CI selbst; „running“ nur mit aktivem Lauf.
    if (items.some((i) => i.state === "verifying" || (i.state === "running" && active.has(i.id))))
      return "run";
    return "wait";
  }
  if (items.some((i) => i.state === "running" || i.state === "verifying" || i.state === "unverified"))
    return "run";
  return "wait";
}

/**
 * Projiziert Auftrag, WorkItems und Entscheidungen auf die Flow Map.
 * Nur belegte Zustände fließen ein; es gibt keine Prozentwerte.
 */
export function projectFlow(
  workOrder: WorkOrder,
  items: readonly WorkItem[],
  decisions: readonly Decision[],
  live?: FlowLive,
): FlowModel {
  const active = live
    ? new Set(
        live.runs
          .filter((r) => ACTIVE_RUN_STATES.includes(r.state) && r.workItemId)
          .map((r) => r.workItemId as string),
      )
    : null;
  const interrupted = WORK_ORDER_INTERRUPTS.has(workOrder.state);
  const effective = interrupted ? workOrder.resumeState : workOrder.state;
  const r = rank(effective);
  const terminalFail = workOrder.state === "FAILED" || workOrder.state === "CANCELLED";
  const openDecisions = decisions.filter((d) => d.state === "PENDING");
  const phase = phaseOf(workOrder);
  const currentIndex = PHASES.findIndex((p) => p.key === phase);
  const released = workOrder.state === "RELEASED";
  const humanNeeded = HUMAN_GATES.has(workOrder.state) || openDecisions.length > 0;

  const steps: StepView[] = PHASES.map((p, i) => ({
    key: p.key,
    label: p.label,
    status: released || i < currentIndex ? "done" : i === currentIndex ? "current" : "pending",
    gate: !released && i === currentIndex && humanNeeded,
  }));

  const at = (state: WorkOrderState): number => ORDER.indexOf(state);
  const stage = (
    runState: WorkOrderState,
    doneFrom: WorkOrderState,
    gateState?: WorkOrderState,
  ): NodeStatus => {
    if (terminalFail) return r >= at(doneFrom) ? "done" : "wait";
    if (gateState && effective === gateState) return "gate";
    if (r >= at(doneFrom)) return "done";
    if (effective === runState) return interrupted ? "block" : "run";
    return "wait";
  };

  const byLane = new Map<Lane, WorkItem[]>();
  for (const item of items) {
    const list = byLane.get(item.lane) ?? [];
    list.push(item);
    byLane.set(item.lane, list);
  }
  const lanes: LaneView[] = [...byLane.entries()].map(([lane, list]) => ({
    lane,
    label: LANE_LABELS[lane],
    done: list.filter((i) => ITEM_DONE.has(i.state)).length,
    total: list.length,
    status: laneStatus(list, active),
    providers: [...new Set(list.map((i) => i.providerProfile))],
  }));

  return {
    phase,
    steps,
    briefing:
      effective === "DRAFT"
        ? live && !live.studioActive
          ? "wait"
          : "run"
        : effective === "SPEC_REVIEW"
          ? "gate"
          : r > at("SPEC_REVIEW")
            ? "done"
            : "wait",
    orchestrator:
      effective === "PLAN_REVIEW"
        ? workOrder.planHash
          ? "gate"
          : live && !live.studioActive
            ? "wait"
            : "run"
        : r >= at("READY")
          ? effective === "RUNNING"
            ? active && (active.size === 0 || interrupted)
              ? "wait"
              : "run"
            : "done"
          : "wait",
    lanes,
    gate: stage("VERIFYING", "REVIEW_READY"),
    pr: stage("VERIFYING", "ACCEPTED", "REVIEW_READY"),
    staging: stage("STAGING", "RELEASE_APPROVAL"),
    production: stage("RELEASE_APPROVAL", "RELEASED", "RELEASE_APPROVAL"),
    openDecisions,
    held: Boolean(workOrder.heldAt) && !released && !terminalFail,
  };
}

/** Entfernt interne Steuer-Marker (z. B. für den Mock-Agenten) aus Texten für Menschen. */
export function displayTitle(title: string): string {
  return title.replace(/\s*\[mock:[a-z]+\]/g, "").trim();
}

/** Aufgabenquote: verifizierte gegenüber genehmigten WorkItems (keine Produkteinschätzung). */
export function taskQuota(items: readonly WorkItem[]): { done: number; total: number } {
  return { done: items.filter((i) => ITEM_DONE.has(i.state)).length, total: items.length };
}
